import { call, put, select } from 'typed-redux-saga';
import { ActionType } from 'typesafe-actions';
import { PatchData } from '@smart-builder/types';
import { applyChangeToBody } from '@smart-builder/util';
import { APIParams, APIResult, callAPI, ResponseCodes } from '../../callApi';
import { emitPromptEvent } from '../../prompt';
import { doCreateAsyncActionResource } from '../util';
import {
  ApiActionType,
  EndPointBuilder,
  FieldChange,
  FieldChangeEntry,
  FieldChangeFormatter,
  HTTPMethod,
  ItemBodyMerger,
  ItemBodyParser,
  ItemSummaryBodyMerger,
  KeyComparer,
  MainResourceHandler,
  MainResourceState,
  MainStateSelector,
  OnSuccessHandler,
  PatchOp,
  QueryParamSelector,
} from './types';
import { buildRequestBody, handleBodyLoaded, handleError } from './util';

type ResultPayload<TItemBody> = {
  frameSeqNumber: number;
  response: APIResult;
  onSuccess?: OnSuccessHandler<TItemBody>;
  onFailure?: (error: any) => void;
};
type UpdateErrorPayload<TItemBody> = ResultPayload<TItemBody> & {
  error: any;
};

function buildPatchBody<TItemBody>(
  pendingChanges: FieldChangeEntry[],
  formatFieldChange: FieldChangeFormatter<TItemBody>,
): any {
  return pendingChanges.map(
    // Intentionally removing boySeqNum
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ bodySeqNum, fieldName, value, op, ...rest }) => ({
      [fieldName]: formatFieldChange(fieldName as any, value),
      op: op === 'update' ? undefined : op, // don't include op if update, assumed by default
      // don't include bodySeqNum
      ...rest,
    }),
  );
}

export function createUpdateAction<
  TItemKey,
  TItemSummary,
  TItemBody,
  TItemBodyAPI,
>(
  displayName: string,
  getEndpoint: EndPointBuilder<TItemKey, TItemSummary, TItemBody>,
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  sameKey: KeyComparer<TItemKey, TItemSummary, TItemBody>,
  parseItemBody: ItemBodyParser<TItemBody, TItemBodyAPI>,
  mergeItemBody: ItemBodyMerger<TItemBody>,
  mergeSummaryBody: ItemSummaryBodyMerger<TItemSummary, TItemBody>,
  formatFieldChange: FieldChangeFormatter<TItemBody>,
  queryParamSelector: QueryParamSelector<TItemBody>,
) {
  const updateAsync = doCreateAsyncActionResource<
    FieldChange<any> & { op: PatchOp },
    ResultPayload<TItemBody>,
    UpdateErrorPayload<TItemBody>
  >(displayName, 'update');

  function* updateRequestSaga(
    action: ActionType<typeof updateAsync.request>,
  ): Generator {
    const { frameSeqNumber, currentBody, currentRecord } = (yield select(
      selector,
    )) as MainResourceState<TItemKey, TItemSummary, TItemBody>;
    const { pendingChanges, loadedLockNum, seqNumFrom } = currentRecord!;
    const pendingChangeCount = pendingChanges.length;
    if (pendingChangeCount > 0) {
      const onSuccess = (body?: TItemBody, patchData?: PatchData) =>
        pendingChanges.forEach(
          ({ onSuccess }) => onSuccess && onSuccess(body, patchData),
        );
      const onFailure = (error: any) =>
        pendingChanges.forEach(
          ({ onFailure }) => onFailure && onFailure(error),
        );

      let response = {} as APIResult;
      try {
        const endpoint = getEndpoint(currentBody);
        const bodyGET = yield* buildRequestBody(selector);

        const bodyPATCH = {
          changes: buildPatchBody(pendingChanges, formatFieldChange),
        };
        const apiParams: APIParams = {
          queryParams: queryParamSelector(currentBody),
          body: { bodyGET, bodyPATCH },
          lockNum: loadedLockNum,
          seqNumFrom,
        };
        response = yield* call(
          callAPI,
          endpoint,
          'PATCH' as HTTPMethod,
          apiParams,
        );

        yield put(
          updateAsync.success({
            frameSeqNumber,
            response,
            onSuccess,
          }),
        );
      } catch (errorObj) {
        if (errorObj?.code === ResponseCodes.sbRequiresPrompt) {
          yield put(
            updateAsync.failure({
              frameSeqNumber,
              response: errorObj.response,
              // Not really an error. Don't put it into the resource, and don't trigger any onFailures.
              error: undefined,
              onFailure: undefined,
            }),
          );

          // we want to display a dialog to the user and replay this update
          // TODO if no listener is registered, need to show an error dialog
          // TODO support more than just update?
          emitPromptEvent({
            ...errorObj.error,
            buildRetryAction: (extraPrompt: any) =>
              updateAsync.request({
                ...action.payload,
                promptAnswers: {
                  ...(action.payload.promptAnswers || {}),
                  ...extraPrompt,
                },
              }),
            onPromptAbort: () => {
              if (onFailure) {
                onFailure(errorObj);
              }
            },
          });
        } else {
          yield put(
            updateAsync.failure({
              frameSeqNumber,
              response: errorObj?.response,
              error: errorObj,
              onFailure,
            }),
          );
        }
      }
    }
  }

  const updateRequestHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof updateAsync.request
  > = (state, action) => {
    const { fieldName, value, op, sequenceNum, ...rest } = action.payload;

    const valueIsSplit =
      value &&
      typeof value === 'object' &&
      value.hasOwnProperty('clientValue') &&
      value.hasOwnProperty('serverUpdateValue');

    const clientValue = valueIsSplit ? value.clientValue : value;

    const { pendingUpdates, currentBody } = state;
    const [newBody, field] =
      op === 'update'
        ? applyChangeToBody(currentBody, fieldName as string, clientValue)
        : [currentBody, undefined];

    let allowUpdate = true;
    if (op === 'update') {
      if (!field) {
        // Allow the update to pass through anyway. This is an update without a corresponding field on the body.
      } else {
        // Only update if the field passed it's validation. This excludes server side warnings.
        allowUpdate = !field.client.validationResult;
      }
    }
    if (allowUpdate) {
      const { pendingChanges, seqNumFrom, seqNumCur } = state.currentRecord!;
      const bodySeqNum =
        1 +
        Math.max(
          // these two will only be different if client has issued changes in the same session but without notifying
          //    our redux, e.g. via a separate tab in the same browser. In which case, pick whatever is higher
          seqNumCur || 0,
          (seqNumFrom || 0) + (pendingChanges?.length || 0),
        );

      // TODO this can be an issue if we start using split values for compound fields
      const serverChangeValue = valueIsSplit
        ? value.serverUpdateValue
        : field
        ? field?.client.value
        : value;

      const change: FieldChangeEntry = {
        fieldName: field ? field.fieldName : fieldName,
        // SS 20231016 undefined values are omitted entirely when posting changes to the server so instead send an empty
        // string which is interpreted as null by the server
        value: serverChangeValue === undefined ? '' : serverChangeValue,
        sequenceNum: field?.client.sequenceNum,
        op,
        bodySeqNum,
        ...rest,
      };

      const currentRecord = {
        ...state.currentRecord!,
        body: newBody,
        pendingChanges: [...state.currentRecord!.pendingChanges, change],
      };

      return {
        ...state,
        currentBody: newBody,
        currentRecord,
        records: state.records.map((r, i) =>
          i === state.currentRecordIndex ? currentRecord : r,
        ),
        lastRecordsChangedAction:
          op === 'save' ? ApiActionType.save : ApiActionType.update,
        pendingUpdates: pendingUpdates + 1,
        error: undefined,
        patchData: undefined,
      };
    } else {
      return {
        ...state,
        currentBody: newBody,
        currentRecord: { ...state.currentRecord!, body: newBody },
      };
    }
  };

  const handleUpdate = (
    state: MainResourceState<TItemKey, TItemSummary, TItemBody>,
    response: APIResult,
    frameSeqNumber: number,
  ) => {
    return handleBodyLoaded(
      state,
      response,
      frameSeqNumber,
      true,
      false,
      true,
      sameKey,
      mergeItemBody,
      mergeSummaryBody,
      parseItemBody,
      state.lastRecordsChangedAction ?? ApiActionType.update,
    );
  };

  const updateSuccessHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof updateAsync.success
  > = (state, action) => {
    const { response, frameSeqNumber } = action.payload;
    return handleUpdate(state, response, frameSeqNumber);
  };

  function* updateSuccessSaga(
    action: ActionType<typeof updateAsync.success>,
  ): Generator {
    const { onSuccess, response } = action.payload;
    if (onSuccess) {
      const { currentBody } = (yield select(selector)) as MainResourceState<
        TItemKey,
        TItemSummary,
        TItemBody
      >;
      onSuccess(currentBody, response.patchData);
    }
  }

  function* updateFailureSaga(
    action: ActionType<typeof updateAsync.failure>,
  ): Generator {
    const { onFailure, error } = action.payload;
    onFailure && onFailure(error);
  }

  const updateFailureHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof updateAsync.failure
  > = (state, action) => {
    const { response, error, frameSeqNumber } = action.payload;
    error && console.error(error);
    if (response?.result) {
      // server returned a result, we should apply it anyway (while not losing track of the error)
      return {
        ...handleUpdate(state, response, frameSeqNumber),
        error,
      };
    } else {
      // TODO still need to clear the onSuccess and onFailures even if no result is returned.
    }

    return handleError<
      TItemKey,
      TItemSummary,
      TItemBody,
      typeof updateAsync.failure
    >(true, state, action);
  };

  return {
    updateAsync,
    updateRequestSaga,
    updateRequestHandler,
    updateSuccessHandler,
    updateSuccessSaga,
    updateFailureHandler,
    updateFailureSaga,
  };
}
